import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { narApi, clientsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const P = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';
const PLM = '#E5D5F5';

const STATUS_COLORS = {
  draft:       { bg: '#F3F4F6', text: '#374151' },
  in_progress: { bg: '#FEF3C7', text: '#92400E' },
  submitted:   { bg: '#D1FAE5', text: '#065F46' },
  reopened:    { bg: '#DBEAFE', text: '#1E40AF' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function NarAssessments() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [nars, setNars] = useState([]);
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');

  const [form, setForm] = useState({ client_id: '', nar_type_id: '', name: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterClient) params.client_id = filterClient;
      const [narsRes, clientsRes, typesRes] = await Promise.all([
        narApi.listNars(params),
        clientsApi.getAll(),
        narApi.listTypes(),
      ]);
      setNars(narsRes.data.nars || []);
      setClients(clientsRes.data?.clients || []);
      setTypes(typesRes.data.nar_types || []);
    } catch (e) {
      toast.error('Failed to load NARs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus, filterClient]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.nar_type_id || !form.name.trim()) {
      toast.error('All fields are required');
      return;
    }
    setCreating(true);
    try {
      const res = await narApi.createNar(form);
      const newNar = res.data.nar;
      toast.success('NAR created');
      setShowCreate(false);
      setForm({ client_id: '', nar_type_id: '', name: '' });
      navigate(`/nar/assessments/${newNar.id}/conduct`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (nar) => {
    if (!window.confirm(`Delete "${nar.name}"? This cannot be undone.`)) return;
    setDeleting(nar.id);
    try {
      await narApi.deleteNar(nar.id);
      toast.success('NAR deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicate = async (nar) => {
    try {
      const res = await narApi.duplicateNar(nar.id, { name: `Copy of ${nar.name}` });
      toast.success('NAR duplicated');
      navigate(`/nar/assessments/${res.data.nar.id}/conduct`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Duplicate failed');
    }
  };

  const btn = (bg, color, border) => ({
    padding: '7px 14px', backgroundColor: bg, color, border: border || 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  });

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a1a2e' }}>
            Network Admin Reviews
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '14px' }}>
            Structured technical assessments with weighted scoring
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isAdmin && (
            <button onClick={() => navigate('/nar/admin')} style={btn('white', PD, `1px solid ${PLM}`)}>
              Manage Catalog
            </button>
          )}
          <button onClick={() => setShowCreate(true)} style={btn(P, 'white')}>
            + New NAR
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151' }}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="submitted">Submitted</option>
          <option value="reopened">Reopened</option>
        </select>
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', color: '#374151' }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(filterStatus || filterClient) && (
          <button onClick={() => { setFilterStatus(''); setFilterClient(''); }}
            style={{ ...btn('white', '#6B7280', '1px solid #D1D5DB') }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>Loading…</div>
      ) : nars.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
          <p style={{ fontSize: '16px' }}>No NARs found. Create one to get started.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: PL, borderBottom: `2px solid ${PLM}` }}>
                {['Client', 'NAR Name', 'Type', 'Status', 'Score', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: PD }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nars.map((n, i) => {
                const sc = STATUS_COLORS[n.status] || STATUS_COLORS.draft;
                return (
                  <tr key={n.id} style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>{n.client_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => navigate(`/nar/assessments/${n.id}/conduct`)}
                        style={{ background: 'none', border: 'none', color: P, fontWeight: '600', fontSize: '14px', cursor: 'pointer', padding: 0 }}>
                        {n.name}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6B7280' }}>{n.nar_type_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: sc.bg, color: sc.text }}>
                        {n.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151' }}>
                      {n.status === 'submitted' || n.status === 'reopened' ? (
                        <button onClick={() => navigate(`/nar/assessments/${n.id}/report`)}
                          style={{ background: 'none', border: 'none', color: P, cursor: 'pointer', fontSize: '13px', padding: 0 }}>
                          View Report
                        </button>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6B7280' }}>
                      {fmtDate(n.submitted_at || n.updated_at || n.created_at)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => navigate(`/nar/assessments/${n.id}/conduct`)}
                          style={btn(PL, PD, `1px solid ${PLM}`)}>
                          Conduct
                        </button>
                        {(n.status === 'submitted' || n.status === 'reopened') && (
                          <button onClick={() => navigate(`/nar/assessments/${n.id}/report`)}
                            style={btn('#D1FAE5', '#065F46', '1px solid #A7F3D0')}>
                            Report
                          </button>
                        )}
                        <button onClick={() => handleDuplicate(n)}
                          style={btn('white', '#374151', '1px solid #D1D5DB')}>
                          Copy
                        </button>
                        {isAdmin && (
                          <button onClick={() => handleDelete(n)} disabled={deleting === n.id}
                            style={btn('#FEE2E2', '#991B1B', '1px solid #FECACA')}>
                            {deleting === n.id ? '…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create NAR Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '480px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: '700', color: PD }}>New Network Admin Review</h2>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#374151' }}>Client *</label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={inputStyle} required>
                  <option value="">Select a client…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#374151' }}>NAR Type *</label>
                <select value={form.nar_type_id} onChange={e => setForm(f => ({ ...f, nar_type_id: e.target.value }))} style={inputStyle} required>
                  <option value="">Select a type…</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: '#374151' }}>Assessment Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Acme Corp — Q2 2025 NAR" style={inputStyle} required />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreate(false)}
                  style={btn('white', '#374151', '1px solid #D1D5DB')}>
                  Cancel
                </button>
                <button type="submit" disabled={creating}
                  style={{ ...btn(P, 'white'), opacity: creating ? 0.7 : 1 }}>
                  {creating ? 'Creating…' : 'Create & Start'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
